/**
 * Parser for the delimited files accepted by the asset-type import.
 */

export interface IParseDelimitedOptions {
  delimiter?: string;
  commentPrefix?: string;
  maxRows?: number;
  trimCells?: boolean;
}

export interface IParseError {
  line: number;
  message: string;
}

export interface IParsedTable {
  header: string[];
  rows: string[][];
  rowLines: number[];
  delimiter: string;
  skipped: { comments: number; empty: number };
  truncated: boolean;
  errors: IParseError[];
}

interface IRawRecord {
  line: number;
  cells: string[];
  firstCellQuoted: boolean;
  hasContent: boolean;
}

export const DEFAULT_MAX_ROWS = 5000;

const BOM_CHAR_CODE = 0xFEFF;
const DEFAULT_COMMENT_PREFIX = '#';
const DELIMITER_CANDIDATES = [',', '\t', ';', '|'];
const UNTERMINATED_QUOTE = 'Unterminated quote — the rest of the file was read as one field.';

const isWhitespace = (char: string): boolean => char.trim() === '';

const stripBom = (input: string): string => (
  input.charCodeAt(0) === BOM_CHAR_CODE ? input.slice(1) : input
);

const countOutsideQuotes = (line: string, target: string): number => {
  let count = 0;
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === target && !inQuotes) {
      count += 1;
    }
    i += 1;
  }

  return count;
};

const firstMeaningfulLine = (text: string, commentPrefix: string): string => {
  const lines = text.split(/\r\n|\r|\n/);
  const found = lines.find((line) => {
    if (line.trim() === '') {
      return false;
    }
    return !(commentPrefix !== '' && line.trimStart().startsWith(commentPrefix));
  });
  return found ?? '';
};

const detectDelimiter = (text: string, commentPrefix: string): string => {
  const line = firstMeaningfulLine(text, commentPrefix);
  let best = DELIMITER_CANDIDATES[0];
  let bestCount = 0;

  DELIMITER_CANDIDATES.forEach((candidate) => {
    const count = countOutsideQuotes(line, candidate);
    if (count > bestCount) {
      bestCount = count;
      best = candidate;
    }
  });

  return best;
};

const tokenize = (
  input: string,
  delimiter: string,
  trimCells: boolean,
): { records: IRawRecord[]; errors: IParseError[] } => {
  const records: IRawRecord[] = [];
  const errors: IParseError[] = [];

  let cells: string[] = [];
  let cell = '';
  let inQuotes = false;
  let cellStart = true;
  let firstCellQuoted = false;
  let recordOpen = false;
  let hasContent = false;
  let line = 1;
  let recordLine = 1;
  let i = 0;

  const endCell = (): void => {
    cells.push(trimCells ? cell.trim() : cell);
    cell = '';
    cellStart = true;
  };

  const endRecord = (): void => {
    endCell();
    records.push({
      line: recordLine, cells, firstCellQuoted, hasContent,
    });
    cells = [];
    firstCellQuoted = false;
    recordOpen = false;
    hasContent = false;
  };

  while (i < input.length) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"' && input[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else if (char === '\r' || char === '\n') {
        if (char === '\r' && input[i + 1] === '\n') {
          i += 1;
        }
        cell += '\n';
        line += 1;
      } else {
        cell += char;
      }
    } else if (char === '"' && cellStart) {
      inQuotes = true;
      cellStart = false;
      recordOpen = true;
      hasContent = true;
      if (cells.length === 0) {
        firstCellQuoted = true;
      }
    } else if (char === delimiter) {
      recordOpen = true;
      hasContent = hasContent || !isWhitespace(char);
      endCell();
    } else if (char === '\r' || char === '\n') {
      if (char === '\r' && input[i + 1] === '\n') {
        i += 1;
      }
      endRecord();
      line += 1;
      recordLine = line;
    } else if (cellStart && trimCells && isWhitespace(char)) {
      recordOpen = true;
    } else {
      cellStart = false;
      recordOpen = true;
      hasContent = hasContent || !isWhitespace(char);
      cell += char;
    }

    i += 1;
  }

  if (inQuotes) {
    errors.push({ line: recordLine, message: UNTERMINATED_QUOTE });
  }

  if (recordOpen) {
    endRecord();
  }

  return { records, errors };
};

const isComment = (record: IRawRecord, commentPrefix: string): boolean => (
  !record.firstCellQuoted
  && record.cells.length > 0
  && record.cells[0].trimStart().startsWith(commentPrefix)
);

export const parseDelimited = (
  input: string,
  options: IParseDelimitedOptions = {},
): IParsedTable => {
  const commentPrefix = options.commentPrefix ?? DEFAULT_COMMENT_PREFIX;
  const trimCells = options.trimCells ?? true;
  const maxRows = options.maxRows ?? DEFAULT_MAX_ROWS;

  const text = stripBom(input);
  const delimiter = options.delimiter ?? detectDelimiter(text, commentPrefix);
  const { records, errors } = tokenize(text, delimiter, trimCells);

  const skipped = { comments: 0, empty: 0 };
  const rows: string[][] = [];
  const rowLines: number[] = [];
  let header: string[] = [];
  let headerSeen = false;
  let truncated = false;

  records.forEach((record) => {
    if (!record.hasContent) {
      skipped.empty += 1;
    } else if (commentPrefix !== '' && isComment(record, commentPrefix)) {
      skipped.comments += 1;
    } else if (!headerSeen) {
      header = record.cells;
      headerSeen = true;
    } else if (rows.length >= maxRows) {
      truncated = true;
    } else {
      rows.push(record.cells);
      rowLines.push(record.line);
    }
  });

  return {
    header, rows, rowLines, delimiter, skipped, truncated, errors,
  };
};
